// POLL-HERD-001 (Jul 2026) — resilient polling hook.
// Fixes the production "thundering herd": multiple components each running a
// bare setInterval(fetch, 30000) fired simultaneously, kept firing while the
// tab was hidden, and stacked overlapping requests whenever the backend was
// slow (each 30s tick spawned a new request even if the last one was still
// pending), amplifying any stall into a request pile-up.
//
// Guarantees:
//   • Staggered start — random jitter so pollers don't tick in lockstep.
//   • Overlap guard — a tick is skipped while the previous one is in flight.
//   • AbortController — in-flight request aborted on unmount / per-tick 25s cap.
//   • Exponential backoff after failures (doubles per failure, capped).
//   • setTimeout chain (not setInterval) — immune to re-render duplication.
//   • Tab-visibility pause — no polls while hidden; instant refresh on return.
//
// The callback receives an AbortSignal: usePoller((signal) => axios.get(url, { signal })).
import { useEffect, useRef } from 'react';

const TICK_TIMEOUT_MS = 25000;

export function usePoller(fn, intervalMs, options = {}) {
  const {
    enabled = true,
    immediate = true,
    pauseWhenHidden = true,
    maxBackoffMs = 5 * 60 * 1000,
  } = options;

  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;

    let stopped = false;
    let timer = null;
    let controller = null;
    let running = false;
    let failures = 0;

    const schedule = (delay) => {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;
      if (pauseWhenHidden && document.visibilityState === 'hidden') {
        schedule(intervalMs);
        return;
      }
      if (running) {
        schedule(intervalMs);
        return;
      }
      running = true;
      controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), Math.min(intervalMs, TICK_TIMEOUT_MS));
      try {
        const result = await fnRef.current(controller.signal);
        // Callbacks that swallow their own errors can return `false` to
        // signal failure so backoff still engages.
        failures = result === false ? failures + 1 : 0;
      } catch (e) {
        failures += 1;
      } finally {
        clearTimeout(timeoutId);
        running = false;
        controller = null;
        const delay = failures > 0
          ? Math.min(intervalMs * Math.pow(2, failures), maxBackoffMs)
          : intervalMs;
        schedule(delay);
      }
    };

    // Stagger: immediate pollers fire within 0-2s; deferred ones (component
    // already did its own initial fetch) land somewhere in [0.5x, 1x] interval.
    const firstDelay = immediate
      ? Math.random() * 2000
      : intervalMs * (0.5 + Math.random() * 0.5);
    schedule(firstDelay);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        failures = 0;
        // jitter so all pollers don't wake in the same instant on tab return
        schedule(250 + Math.random() * 2000);
      }
    };
    if (pauseWhenHidden) document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      clearTimeout(timer);
      if (controller) controller.abort();
      if (pauseWhenHidden) document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs, immediate, pauseWhenHidden, maxBackoffMs]);
}

export default usePoller;
